/*
 * Sample data for the IA Overhaul prototype. Hard-coded — the prototype does
 * not read from real models. Names mimic plausible nonprofit campaigns so
 * stakeholders can mentally substitute their own.
 */

export type TSampleCampaignChannel = 'email' | 'ads' | 'search' | 'monetization';
export type TSampleCampaignType =
  | 'Affinity'
  | 'Single Send'
  | 'Auto Send'
  | 'Pixl Plus'
  | 'Meta Email Mapping'
  | 'Search Keyword'
  | 'Custom Objective';

export interface ISampleCampaign {
  channel: TSampleCampaignChannel;
  clicks: number;
  conversions: number;
  dateCreated: string;
  dateEnd: string | null;
  dateStart: string | null;
  id: string;
  name: string;
  objective: string;
  project: string | null;
  status: 'draft' | 'live' | 'paused' | 'sent' | 'completed';
  type: TSampleCampaignType;
  views: number;
}

export const sampleCampaigns: ISampleCampaign[] = [
  {
    id: 'c-1',
    name: 'Spring appeal — early bird',
    objective: 'Single Send Email',
    channel: 'email',
    type: 'Single Send',
    project: 'Spring 2026 appeal',
    status: 'sent',
    dateCreated: '2026-04-15',
    dateStart: '2026-04-22',
    dateEnd: '2026-04-22',
    views: 8420,
    clicks: 612,
    conversions: 142,
  },
  {
    id: 'c-2',
    name: 'Spring appeal — main send',
    objective: 'Single Send Email',
    channel: 'email',
    type: 'Single Send',
    project: 'Spring 2026 appeal',
    status: 'draft',
    dateCreated: '2026-04-22',
    dateStart: null,
    dateEnd: null,
    views: 0,
    clicks: 0,
    conversions: 0,
  },
  {
    id: 'c-3',
    name: 'Awareness retargeting',
    objective: 'Build Reach and Awareness',
    channel: 'ads',
    type: 'Affinity',
    project: 'Spring 2026 appeal',
    status: 'live',
    dateCreated: '2026-04-04',
    dateStart: '2026-04-10',
    dateEnd: '2026-06-30',
    views: 24150,
    clicks: 412,
    conversions: 86,
  },
  {
    id: 'c-4',
    name: 'Donor brand search',
    objective: 'Drive Conversions',
    channel: 'search',
    type: 'Search Keyword',
    project: null,
    status: 'live',
    dateCreated: '2026-04-12',
    dateStart: '2026-04-15',
    dateEnd: '2026-07-15',
    views: 1820,
    clicks: 318,
    conversions: 24,
  },
  {
    id: 'c-5',
    name: 'Volunteer recruitment',
    objective: 'Auto Send Email',
    channel: 'email',
    type: 'Auto Send',
    project: null,
    status: 'paused',
    dateCreated: '2026-03-20',
    dateStart: '2026-04-01',
    dateEnd: null,
    views: 3210,
    clicks: 142,
    conversions: 41,
  },
  {
    id: 'c-6',
    name: 'Gala save-the-date',
    objective: 'Single Send Email',
    channel: 'email',
    type: 'Single Send',
    project: 'Annual gala',
    status: 'sent',
    dateCreated: '2026-02-28',
    dateStart: '2026-03-12',
    dateEnd: '2026-03-12',
    views: 5400,
    clicks: 824,
    conversions: 312,
  },
  {
    id: 'c-7',
    name: 'Gala display ads',
    objective: 'Build Reach and Awareness',
    channel: 'ads',
    type: 'Pixl Plus',
    project: 'Annual gala',
    status: 'completed',
    dateCreated: '2026-03-05',
    dateStart: '2026-03-15',
    dateEnd: '2026-04-04',
    views: 18200,
    clicks: 612,
    conversions: 67,
  },
  {
    id: 'c-8',
    name: 'Newsletter monetization',
    objective: 'Custom Objective',
    channel: 'monetization',
    type: 'Custom Objective',
    project: null,
    status: 'live',
    dateCreated: '2026-03-22',
    dateStart: '2026-04-01',
    dateEnd: null,
    views: 12400,
    clicks: 248,
    conversions: 0,
  },
];

export interface ISampleSegment {
  description: string;
  id: string;
  name: string;
  size: number;
  source: 'pixel' | 'crm' | 'manual';
}

export const sampleSegments: ISampleSegment[] = [
  {
    id: 's-1',
    name: 'Lapsed donors (12+ months)',
    size: 4218,
    description: 'Donors who have not given in the past 12 months.',
    source: 'crm',
  },
  {
    id: 's-2',
    name: 'Monthly sustainers',
    size: 612,
    description: 'Active recurring donors.',
    source: 'crm',
  },
  {
    id: 's-3',
    name: 'Event attendees — last 24 months',
    size: 2104,
    description: 'Attended at least one in-person or virtual event.',
    source: 'manual',
  },
  {
    id: 's-4',
    name: 'High-engagement email subscribers',
    size: 8830,
    description: 'Opened or clicked at least 5 emails in the last quarter.',
    source: 'pixel',
  },
  {
    id: 's-5',
    name: 'Donation page visitors — last 30 days',
    size: 2456,
    description: 'Visited the donation page but did not give.',
    source: 'pixel',
  },
];

export interface ISamplePerson {
  email: string;
  engagement: 'high' | 'medium' | 'low';
  id: string;
  lastSeen: string;
  name: string;
  status: 'identified' | 'subscribed' | 'unsubscribed';
}

export const samplePeople: ISamplePerson[] = [
  {
    id: 'p-1',
    name: 'Jordan Patel',
    email: 'jordan.patel@example.com',
    status: 'subscribed',
    engagement: 'high',
    lastSeen: '2026-05-02',
  },
  {
    id: 'p-2',
    name: 'Marisa Chen',
    email: 'marisa.chen@example.com',
    status: 'subscribed',
    engagement: 'medium',
    lastSeen: '2026-05-01',
  },
  {
    id: 'p-3',
    name: 'Tom Avery',
    email: 'tom.avery@example.com',
    status: 'identified',
    engagement: 'low',
    lastSeen: '2026-04-12',
  },
  {
    id: 'p-4',
    name: 'Reese Williams',
    email: 'reese.w@example.com',
    status: 'subscribed',
    engagement: 'high',
    lastSeen: '2026-05-03',
  },
  {
    id: 'p-5',
    name: 'Hiro Tanaka',
    email: 'hiro.tanaka@example.com',
    status: 'unsubscribed',
    engagement: 'low',
    lastSeen: '2026-03-28',
  },
  {
    id: 'p-6',
    name: 'Aisha Mohammed',
    email: 'aisha.m@example.com',
    status: 'subscribed',
    engagement: 'medium',
    lastSeen: '2026-04-29',
  },
];

export interface ISampleImport {
  id: string;
  name: string;
  recordCount: number;
  source: 'csv' | 'salesforce' | 'raisers-edge' | 'stripe';
  startedAt: string;
  status: 'completed' | 'in-progress' | 'failed';
}

export const sampleImports: ISampleImport[] = [
  {
    id: 'i-1',
    name: 'Salesforce NPSP nightly sync',
    source: 'salesforce',
    status: 'completed',
    recordCount: 18420,
    startedAt: '2026-05-04 02:00',
  },
  {
    id: 'i-2',
    name: 'Spring appeal CSV',
    source: 'csv',
    status: 'completed',
    recordCount: 1250,
    startedAt: '2026-04-22 10:14',
  },
  {
    id: 'i-3',
    name: "Raiser's Edge weekly",
    source: 'raisers-edge',
    status: 'in-progress',
    recordCount: 4218,
    startedAt: '2026-05-04 05:00',
  },
  {
    id: 'i-4',
    name: 'Stripe transactions',
    source: 'stripe',
    status: 'completed',
    recordCount: 312,
    startedAt: '2026-05-03 23:00',
  },
  {
    id: 'i-5',
    name: 'Volunteer fair CSV',
    source: 'csv',
    status: 'failed',
    recordCount: 0,
    startedAt: '2026-04-29 14:22',
  },
];

export interface ISampleConversion {
  count: number;
  id: string;
  lastTracked: string;
  name: string;
  type: 'pixel' | 'form' | 'donation';
  value: number;
}

export const sampleConversions: ISampleConversion[] = [
  {
    id: 'cv-1',
    name: 'Donation completed',
    type: 'donation',
    count: 1842,
    value: 124550,
    lastTracked: '2026-05-04',
  },
  {
    id: 'cv-2',
    name: 'Newsletter signup',
    type: 'form',
    count: 643,
    value: 0,
    lastTracked: '2026-05-04',
  },
  {
    id: 'cv-3',
    name: 'Volunteer interest form',
    type: 'form',
    count: 218,
    value: 0,
    lastTracked: '2026-05-03',
  },
  {
    id: 'cv-4',
    name: 'Donation page view',
    type: 'pixel',
    count: 12420,
    value: 0,
    lastTracked: '2026-05-04',
  },
  {
    id: 'cv-5',
    name: 'Event RSVP',
    type: 'form',
    count: 312,
    value: 0,
    lastTracked: '2026-05-02',
  },
];

export type TSampleTemplateType =
  | 'Email'
  | 'Invite Banner'
  | 'Invite email'
  | 'Invite page'
  | 'Landing page';

export interface ISampleTemplate {
  group: string;
  id: string;
  lastUpdated: string;
  name: string;
  type: TSampleTemplateType;
}

export const sampleTemplates: ISampleTemplate[] = [
  {
    id: 't-1',
    name: 'Newsletter base template',
    type: 'Email',
    group: 'Newsletters',
    lastUpdated: '2026-04-30',
  },
  {
    id: 't-2',
    name: 'Sustainer welcome series',
    type: 'Email',
    group: 'Welcome series',
    lastUpdated: '2026-04-18',
  },
  {
    id: 't-3',
    name: 'Donation receipt',
    type: 'Email',
    group: 'Receipts',
    lastUpdated: '2026-05-03',
  },
  {
    id: 't-4',
    name: 'Spring gala invite — banner',
    type: 'Invite Banner',
    group: 'Event invites',
    lastUpdated: '2026-04-22',
  },
  {
    id: 't-5',
    name: 'Spring gala invite — email',
    type: 'Invite email',
    group: 'Event invites',
    lastUpdated: '2026-04-12',
  },
  {
    id: 't-6',
    name: 'Volunteer thank-you',
    type: 'Email',
    group: 'Confirmations',
    lastUpdated: '2026-03-28',
  },
  {
    id: 't-7',
    name: 'Year-end appeal — main',
    type: 'Email',
    group: 'Appeals',
    lastUpdated: '2025-12-12',
  },
  {
    id: 't-8',
    name: 'Spring gala invite — RSVP page',
    type: 'Invite page',
    group: 'Event invites',
    lastUpdated: '2026-04-15',
  },
  {
    id: 't-9',
    name: 'Year-end appeal — landing page',
    type: 'Landing page',
    group: 'Appeals',
    lastUpdated: '2025-12-08',
  },
];

export type TSampleCreativeCategory = 'Display' | 'Meta';
export type TSampleCreativeType =
  | 'Image'
  | 'Banner Builder'
  | 'Ad Tag'
  | 'Facebook Image'
  | 'Facebook Video';

export interface ISampleCreative {
  archived: boolean;
  attachedCampaignIds: string[];
  category: TSampleCreativeCategory;
  creativeType: TSampleCreativeType;
  dateCreated: string;
  id: string;
  name: string;
}

export const sampleCreatives: ISampleCreative[] = [
  {
    id: 'cr-1',
    name: 'Spring appeal hero',
    category: 'Display',
    creativeType: 'Image',
    dateCreated: '2026-03-30',
    attachedCampaignIds: ['c-1', 'c-3', 'c-5'],
    archived: false,
  },
  {
    id: 'cr-2',
    name: 'Awareness display ad',
    category: 'Display',
    creativeType: 'Image',
    dateCreated: '2026-03-15',
    attachedCampaignIds: ['c-3', 'c-7'],
    archived: false,
  },
  {
    id: 'cr-3',
    name: 'Sustainer signup banner',
    category: 'Display',
    creativeType: 'Banner Builder',
    dateCreated: '2026-02-10',
    attachedCampaignIds: ['c-5'],
    archived: false,
  },
  {
    id: 'cr-4',
    name: 'Newsletter banner — Q2',
    category: 'Display',
    creativeType: 'Ad Tag',
    dateCreated: '2026-04-08',
    attachedCampaignIds: ['c-8'],
    archived: false,
  },
  {
    id: 'cr-5',
    name: 'Annual gala — square',
    category: 'Meta',
    creativeType: 'Facebook Image',
    dateCreated: '2026-03-04',
    attachedCampaignIds: ['c-7'],
    archived: false,
  },
  {
    id: 'cr-6',
    name: 'Volunteer drive — feed',
    category: 'Meta',
    creativeType: 'Facebook Image',
    dateCreated: '2026-05-04',
    attachedCampaignIds: [],
    archived: false,
  },
  {
    id: 'cr-7',
    name: 'Annual gala teaser',
    category: 'Meta',
    creativeType: 'Facebook Video',
    dateCreated: '2026-03-20',
    attachedCampaignIds: ['c-6', 'c-7'],
    archived: false,
  },
  {
    id: 'cr-8',
    name: 'Holiday appeal — retired',
    category: 'Display',
    creativeType: 'Image',
    dateCreated: '2025-11-12',
    attachedCampaignIds: [],
    archived: true,
  },
];

export type TSampleFormStatus = 'draft' | 'published';

export interface ISampleForm {
  attachedCampaignIds: string[];
  id: string;
  lastUpdated: string;
  name: string;
  status: TSampleFormStatus;
  submissions: number;
  views: number;
}

export const sampleForms: ISampleForm[] = [
  {
    id: 'fm-1',
    name: 'Donation form — $25 default',
    status: 'published',
    views: 2456,
    submissions: 123,
    attachedCampaignIds: ['c-1', 'c-2', 'c-6'],
    lastUpdated: '2026-04-22',
  },
  {
    id: 'fm-2',
    name: 'Volunteer interest form',
    status: 'published',
    views: 890,
    submissions: 64,
    attachedCampaignIds: ['c-5'],
    lastUpdated: '2026-04-15',
  },
  {
    id: 'fm-3',
    name: 'Newsletter signup',
    status: 'published',
    views: 12420,
    submissions: 643,
    attachedCampaignIds: ['c-1', 'c-3', 'c-5', 'c-8'],
    lastUpdated: '2026-05-02',
  },
  {
    id: 'fm-4',
    name: 'Event RSVP form',
    status: 'published',
    views: 1240,
    submissions: 312,
    attachedCampaignIds: ['c-6'],
    lastUpdated: '2026-04-08',
  },
  {
    id: 'fm-5',
    name: 'Survey — supporter feedback',
    status: 'draft',
    views: 0,
    submissions: 0,
    attachedCampaignIds: [],
    lastUpdated: '2026-05-04',
  },
  {
    id: 'fm-6',
    name: 'Sustainer pledge form',
    status: 'draft',
    views: 0,
    submissions: 0,
    attachedCampaignIds: [],
    lastUpdated: '2026-04-30',
  },
];

export const sampleDonationForms: ISampleForm[] = [
  {
    id: 'df-1',
    name: 'General donation — $25 default',
    status: 'published',
    views: 8240,
    submissions: 412,
    attachedCampaignIds: ['c-1', 'c-2'],
    lastUpdated: '2026-04-22',
  },
  {
    id: 'df-2',
    name: 'Spring appeal — donation form',
    status: 'published',
    views: 4126,
    submissions: 287,
    attachedCampaignIds: ['c-1', 'c-3'],
    lastUpdated: '2026-04-18',
  },
  {
    id: 'df-3',
    name: 'Sustainer monthly giving',
    status: 'published',
    views: 1840,
    submissions: 64,
    attachedCampaignIds: ['c-5'],
    lastUpdated: '2026-04-12',
  },
  {
    id: 'df-4',
    name: 'Annual gala — pledge form',
    status: 'published',
    views: 612,
    submissions: 94,
    attachedCampaignIds: ['c-6'],
    lastUpdated: '2026-03-28',
  },
  {
    id: 'df-5',
    name: 'Year-end matching gift',
    status: 'draft',
    views: 0,
    submissions: 0,
    attachedCampaignIds: [],
    lastUpdated: '2026-05-01',
  },
];

export interface ISampleDonationTransaction {
  amount: number;
  campaign: string | null;
  date: string;
  donor: string;
  formId: string;
  formName: string;
  growthCredits: number;
  id: string;
  status: 'completed' | 'refunded' | 'pending';
}

export const sampleDonationTransactions: ISampleDonationTransaction[] = [
  {
    id: 'dt-1',
    donor: 'Sarah Mitchell',
    amount: 250,
    growthCredits: 250,
    formId: 'df-2',
    formName: 'Spring appeal — donation form',
    campaign: 'Spring appeal — early bird',
    date: '2026-05-05',
    status: 'completed',
  },
  {
    id: 'dt-2',
    donor: 'James Patel',
    amount: 50,
    growthCredits: 50,
    formId: 'df-1',
    formName: 'General donation — $25 default',
    campaign: null,
    date: '2026-05-05',
    status: 'completed',
  },
  {
    id: 'dt-3',
    donor: 'Linda Okafor',
    amount: 25,
    growthCredits: 25,
    formId: 'df-3',
    formName: 'Sustainer monthly giving',
    campaign: 'Monthly sustainer drive',
    date: '2026-05-04',
    status: 'completed',
  },
  {
    id: 'dt-4',
    donor: 'David Chen',
    amount: 1000,
    growthCredits: 1000,
    formId: 'df-4',
    formName: 'Annual gala — pledge form',
    campaign: 'Annual gala 2026',
    date: '2026-05-04',
    status: 'completed',
  },
  {
    id: 'dt-5',
    donor: 'Maria Hernandez',
    amount: 100,
    growthCredits: 100,
    formId: 'df-2',
    formName: 'Spring appeal — donation form',
    campaign: 'Spring appeal — early bird',
    date: '2026-05-03',
    status: 'completed',
  },
  {
    id: 'dt-6',
    donor: 'Robert Lee',
    amount: 75,
    growthCredits: 0,
    formId: 'df-1',
    formName: 'General donation — $25 default',
    campaign: null,
    date: '2026-05-03',
    status: 'refunded',
  },
  {
    id: 'dt-7',
    donor: 'Anonymous',
    amount: 500,
    growthCredits: 500,
    formId: 'df-2',
    formName: 'Spring appeal — donation form',
    campaign: 'Spring appeal — early bird',
    date: '2026-05-02',
    status: 'completed',
  },
  {
    id: 'dt-8',
    donor: 'Priya Shah',
    amount: 25,
    growthCredits: 25,
    formId: 'df-3',
    formName: 'Sustainer monthly giving',
    campaign: 'Monthly sustainer drive',
    date: '2026-05-02',
    status: 'completed',
  },
];

export interface ISampleReport {
  delta: string;
  id: string;
  name: string;
  scope: 'account' | 'project' | 'flight';
  value: string;
}

export const sampleReports: ISampleReport[] = [
  {
    id: 'r-1',
    name: 'Total donations — last 30 days',
    value: '$48,210',
    delta: '+12.4%',
    scope: 'account',
  },
  {
    id: 'r-2',
    name: 'Spring 2026 appeal — raised',
    value: '$22,540',
    delta: '+38%',
    scope: 'project',
  },
  {
    id: 'r-3',
    name: 'Email open rate — last 30 days',
    value: '34.2%',
    delta: '-1.1pp',
    scope: 'account',
  },
  {
    id: 'r-4',
    name: 'Awareness retargeting — clicks',
    value: '1,802',
    delta: '+18%',
    scope: 'flight',
  },
  {
    id: 'r-5',
    name: 'New email subscribers — last 30 days',
    value: '643',
    delta: '+5%',
    scope: 'account',
  },
  {
    id: 'r-6',
    name: 'Annual gala — RSVPs',
    value: '312',
    delta: '+22%',
    scope: 'project',
  },
  {
    id: 'r-7',
    name: 'Donor brand search — clicks',
    value: '4,210',
    delta: '+9%',
    scope: 'flight',
  },
  {
    id: 'r-8',
    name: 'Conversion rate — donation page',
    value: '4.8%',
    delta: '+0.6pp',
    scope: 'account',
  },
  {
    id: 'r-9',
    name: 'Annual gala — raised',
    value: '$94,300',
    delta: '+14%',
    scope: 'project',
  },
];

export interface ISampleProjectDetail {
  campaignCount: number;
  description: string;
  endDate: string | null;
  id: string;
  name: string;
  raised: number | null;
  startDate: string;
  status: 'active' | 'planning' | 'completed';
}

export const sampleProjectDetails: ISampleProjectDetail[] = [
  {
    id: 'pr-1',
    name: 'Spring 2026 appeal',
    description: 'Q2 fundraising appeal across email, ads, and search.',
    status: 'active',
    startDate: '2026-04-01',
    endDate: '2026-06-30',
    campaignCount: 3,
    raised: 22540,
  },
  {
    id: 'pr-2',
    name: 'Annual gala',
    description: 'Save-the-date, RSVPs, and post-event thank-you.',
    status: 'active',
    startDate: '2026-03-01',
    endDate: '2026-05-15',
    campaignCount: 2,
    raised: 94300,
  },
  {
    id: 'pr-3',
    name: 'Volunteer drive',
    description: 'Recruit volunteers for summer programs.',
    status: 'planning',
    startDate: '2026-05-15',
    endDate: null,
    campaignCount: 0,
    raised: null,
  },
];

export interface ISamplePartner {
  contact: string;
  id: string;
  name: string;
  optedIn: boolean;
  role: 'exhibitor' | 'sponsor' | 'speaker';
}

export const samplePartners: ISamplePartner[] = [
  {
    id: 'pa-1',
    name: 'Acme Robotics',
    role: 'exhibitor',
    contact: 'jamie@acmerobotics.com',
    optedIn: true,
  },
  {
    id: 'pa-2',
    name: 'Patel & Associates Foundation',
    role: 'sponsor',
    contact: 'press@patelfoundation.org',
    optedIn: true,
  },
  {
    id: 'pa-3',
    name: 'Dr. Reese Williams',
    role: 'speaker',
    contact: 'reese@example.com',
    optedIn: true,
  },
  {
    id: 'pa-4',
    name: 'Northwest Mfg',
    role: 'exhibitor',
    contact: 'marketing@nwmfg.com',
    optedIn: false,
  },
  {
    id: 'pa-5',
    name: 'Hiro Tanaka',
    role: 'speaker',
    contact: 'hiro@example.com',
    optedIn: true,
  },
];

export interface ISampleInvitesCampaign {
  acceptedPartners: number;
  id: string;
  name: string;
  project: string;
  reach: number;
  sentPartners: number;
  status: 'draft' | 'live' | 'completed';
  totalPartners: number;
}

export const sampleInvitesCampaigns: ISampleInvitesCampaign[] = [
  {
    id: 'ic-1',
    name: 'Annual gala — exhibitor invites',
    project: 'Annual gala',
    status: 'live',
    totalPartners: 42,
    sentPartners: 42,
    acceptedPartners: 28,
    reach: 14210,
  },
  {
    id: 'ic-2',
    name: 'Annual gala — sponsor co-promo',
    project: 'Annual gala',
    status: 'live',
    totalPartners: 8,
    sentPartners: 8,
    acceptedPartners: 6,
    reach: 8430,
  },
  {
    id: 'ic-3',
    name: 'Spring summit — speaker amplify',
    project: 'Spring 2026 appeal',
    status: 'completed',
    totalPartners: 12,
    sentPartners: 12,
    acceptedPartners: 11,
    reach: 5240,
  },
  {
    id: 'ic-4',
    name: 'Volunteer fair — community partners',
    project: 'Volunteer drive',
    status: 'draft',
    totalPartners: 18,
    sentPartners: 0,
    acceptedPartners: 0,
    reach: 0,
  },
];

export interface ISampleFunnelStep {
  count: number;
  label: string;
}

export interface ISampleFunnel {
  description: string;
  id: string;
  name: string;
  steps: ISampleFunnelStep[];
}

export const sampleFunnels: ISampleFunnel[] = [
  {
    id: 'f-1',
    name: 'Donation funnel — Spring 2026 appeal',
    description: 'From first ad impression to completed donation.',
    steps: [
      { label: 'Ad impressions', count: 24150 },
      { label: 'Ad clicks', count: 4210 },
      { label: 'Landing page views', count: 1802 },
      { label: 'Donation page views', count: 612 },
      { label: 'Donations completed', count: 142 },
    ],
  },
  {
    id: 'f-2',
    name: 'Email engagement — main appeal',
    description: 'From email send to donation.',
    steps: [
      { label: 'Sent', count: 12420 },
      { label: 'Opened', count: 4250 },
      { label: 'Clicked', count: 612 },
      { label: 'Donation page views', count: 312 },
      { label: 'Donations completed', count: 86 },
    ],
  },
];
